import MovimentacaoInternaStore from '@/store/MovimentacaoInternaStore';
import RouterStore from '@/store/RouterStore';
import { runInAction } from 'mobx';
import { Minuta, Processo, TipoControle } from '../modelo';
import { SituacaoMinuta } from '../modelo/SituacaoMinuta';
import EscaninhoStore from '../store/EscaninhoStore';
import ParametroStore from '../store/ParametroStore';
import CategoriaService from './CategoriaService';
import InformacoesProcessoService from './InformacoesProcessoService';
import MinutaService from './MinutaService';
import MovimentoService from './MovimentoService';

class PendenciasProcessoService {
    private readonly IDS_REGRA_SEM_MINUTA_PENDENTE = [
        'NAO_POSSUI_MINUTA_PENDENTE'
    ];

    pendenciasProcesso() {


        const proximoCard = MovimentacaoInternaStore.proximoCard;

        // considerar -2 como válido (manter movimentação atual)
        const semDestinoValido = (
            proximoCard === undefined ||
            proximoCard === null ||
            proximoCard === -1
        );

        if (!MovimentacaoInternaStore.openModalProximaTarefa && semDestinoValido) {

            EscaninhoStore.processosSelecionados.forEach(processo => {
                runInAction(() => {
                    processo.pendencia = 'O destino da movimentação não foi escolhida ou não existe.';
                });
            });

        } else {
            this.pendenciasTarefa();
        }
    }

    pendenciasTarefa(processosExternosSelecionados?: Processo[]) {

        const proxControle = MovimentacaoInternaStore.controleProximoCard;

        // 🚨 CORREÇÃO 3: permitir -2 (lote mantendo movimentação)
        if (!proxControle && MovimentacaoInternaStore.proximoCard !== -2) {
            return;
        }

        const regrasPossuiMinuta = proxControle?.regrasPossuiMinuta?.();

        let processos = MovimentacaoInternaStore.processo
            ? [MovimentacaoInternaStore.processo]
            : EscaninhoStore.processosSelecionados;

        if (RouterStore.isControleGabinete) {
            processos = processosExternosSelecionados;
        }

        // Em lote com "manter movimentação atual" (-2), não deve existir pendência de movimentação.
        if (MovimentacaoInternaStore.proximoCard === -2) {
            processos.forEach((processo: Processo) => {
                runInAction(() => {
                    processo.pendencia = '';
                });
            });
            return;
        }

        processos.forEach(async (processo: Processo) => {

            let pendencia = '';

            // 🚨 CORREÇÃO 4: só valida destino se NÃO for -2
            if (!proxControle && MovimentacaoInternaStore.proximoCard !== -2) {
                pendencia += 'O destino da movimentação não foi escolhida ou não existe.';
            }
            else if (this.possuiRegraSemMinutaPendente(proxControle) && this.processoPossuiMinutaPendente(processo)) {
                pendencia += 'Este processo possui minuta pendente. Para movimentar, todas as minutas devem estar arquivadas ou assinadas.';
            }

            else if (regrasPossuiMinuta && regrasPossuiMinuta.length === 1) {

                const tipoMinuta = regrasPossuiMinuta[0].valor
                    ? parseInt(regrasPossuiMinuta[0].valor)
                    : undefined;

                if (!tipoMinuta) {

                    if (!processo.minutaSelecionada) {

                        const minutas = processo.minutas;

                        if (minutas.length < 1) {
                            pendencia += 'Não foi encontrada minuta que possa ser associada a esta movimentação. Por favor crie uma minuta e tente novamente.';
                        }

                    } else {
                        pendencia += await this.outrasPendencias(processo, proxControle);
                    }

                } else {

                    const docEncontrado = processo.minutas && processo.minutas.find(minuta => {
                        return minuta.isTipo(tipoMinuta);
                    });

                    const descricaoTipo = ParametroStore.tiposMinuta.find(tipo => {
                        return tipo.codigo === tipoMinuta;
                    })?.descricao;

                    if (docEncontrado) {

                        if (processo.minutaSelecionada && processo.tipoMinutaSelecionada !== tipoMinuta) {
                            pendencia += 'Esta movimentação exige uma minuta do tipo ' + descricaoTipo + '.';
                        } else {
                            pendencia += await this.outrasPendencias(processo, proxControle);
                        }

                    } else {
                        pendencia += 'Este processo não possui uma minuta compatível (' + descricaoTipo + ').';
                    }
                }

            } else {
                pendencia += await this.outrasPendencias(processo, proxControle);
            }

            runInAction(() => {
                processo.pendencia = pendencia;
            });
        });
    }

    private possuiRegraSemMinutaPendente = (proxControle): boolean => {
        const regrasAssociacao = proxControle?.configControle?.regrasAssociacao || [];

        return regrasAssociacao.some((regra) => {
            const id = regra?.tipoRegra?.id || regra?.id || '';
            const descricao = regra?.tipoRegra?.descricao || '';
            const valor = regra?.valor;

            const regraPorIdOuDescricao = this.IDS_REGRA_SEM_MINUTA_PENDENTE.includes(id)
                || (/minuta/i.test(descricao) && /pendente/i.test(descricao));

            const regraEstaAtiva = valor === undefined || valor === null || valor === true || valor === 'true';

            return regraPorIdOuDescricao && regraEstaAtiva;
        });
    }

    private processoPossuiMinutaPendente = (processo: Processo): boolean => {
        return (processo?.minutas || []).some((minuta) => {
            const codigoSituacao = minuta?.situacaoMinuta?.codigo;
            return codigoSituacao !== SituacaoMinuta.ARQUIVADA
                && codigoSituacao !== SituacaoMinuta.ASSINADA;
        });
    }

    private outrasPendencias = async (processo, proxControle) => {

        const minuta = processo.minutas?.find(m => m.numero === processo.minutaSelecionada);
        const pendencias = [];

        if (minuta) {

            const pendenciaCategoria = await this.conferirComplementosDaCategoria(minuta, proxControle, processo.identificadorProcesso);
            const pendenciaMinuta = await this.verificarMinutaDocx(minuta);

            if (pendenciaCategoria) pendencias.push(pendenciaCategoria);
            if (pendenciaMinuta) pendencias.push(
                pendenciaMinuta.replace(/,[\s]*([^,]+)$/, ' e $1') +
                '. Confira a minuta no seu editor de texto e tente novamente.'
            );
        }

        return pendencias.join(', ');
    }

    private verificarMinutaDocx = async (minuta: Minuta) => {
        let pendencia = '';

        if (minuta && minuta.isTipoDocumentoMinutaDocx()) {
            pendencia = await MinutaService.verificarMinutaDocx(minuta.numero);
        }

        return pendencia === 'OK' ? '' : pendencia;
    }

    private conferirComplementosDaCategoria = async (minuta, proxControle, idProcesso) => {

        let pendencia = '';

        if (proxControle?.tipo.id === TipoControle.ASSINATURA) {

            let categoria = await CategoriaService.buscarPorMinuta(idProcesso, minuta.tipo.codigo) as any;

            if (!categoria || categoria.length === 0) {
                return pendencia;
            }

            categoria = categoria[0];

            const movimentoCategoria = await this.carregarMovimentoCategoria(categoria);

            let complementos = this.carregarComplementos(movimentoCategoria, idProcesso);

            const conteudoComplementos = await this.carregarConteudoComplementos(
                idProcesso,
                minuta.tipo.codigo,
                categoria ? categoria.numero : undefined
            );

            complementos = complementos.map(complemento => {
                const conteudo = conteudoComplementos.find(cont => cont.nome === complemento.nome);
                complemento.conteudo = conteudo ? conteudo.conteudo : '';
                return complemento;
            });

            let preenchidos = true;

            complementos.forEach(complemento => {
                preenchidos = preenchidos && complemento.conteudo !== undefined && complemento.conteudo.length > 0;
            });

            if (!preenchidos) {
                pendencia = 'Esta minuta possui uma categoria que exige um complemento. Por favor arrume o complemento no editor e tente novamente.';
            }
        }

        return pendencia;
    }

    private carregarMovimentoCategoria = async (categoria) => {
        if (categoria && categoria.codigoMovimentoCnj) {
            const movimentoCNJ = await MovimentoService.recuperar(categoria.codigoMovimentoCnj);
            return movimentoCNJ;
        }
        return undefined;
    }

    private carregarComplementos = (movimento, idProcesso) => {
        if (movimento && movimento.aplicacaoMovimentoList[0].aplicacaoComplementoList) {
            const complementos = movimento.aplicacaoMovimentoList[0].aplicacaoComplementoList.map(
                (complemento) => complemento.tipoComplemento
            );

            complementos.forEach((complemento) => {
                this.carregarComboValoresComplementos(complemento, idProcesso);
            });

            return complementos;
        }

        return [];
    }

    private carregarConteudoComplementos = async (_idProcesso, _tipoMinuta, _idCategoria) => {
        const conteudoComplementos = [];
        return conteudoComplementos;
    }

    private carregarComboValoresComplementos = async (complemento, idProcesso) => {

        if (complemento.nome === 'nome_da_parte') {

            const partes = await InformacoesProcessoService.consultarPartesProcesso(idProcesso);

            complemento.valoresComplemento = [];

            if (partes.ATIVO) {
                partes.ATIVO.forEach((parte) => {
                    if (!complemento.valoresComplemento.includes(parte.nome)) {
                        complemento.valoresComplemento.push(parte.nome);
                    }
                });
            }

            if (partes.PASSIVO) {
                partes.PASSIVO.forEach((parte) => {
                    if (!complemento.valoresComplemento.includes(parte.nome)) {
                        complemento.valoresComplemento.push(parte.nome);
                    }
                });
            }

            if (partes.TERCEIROS) {
                partes.TERCEIROS.forEach((parte) => {
                    if (!complemento.valoresComplemento.includes(parte.nome)) {
                        complemento.valoresComplemento.push(parte.nome);
                    }
                });
            }
        }
    }

    limpaPendenciasProcessosSelecionados = () => {
        const processos = [...EscaninhoStore.processosSelecionados];

        if (MovimentacaoInternaStore.processo) {
            processos.push(MovimentacaoInternaStore.processo);
        }

        const processosUnicos = processos.filter((processo, index, array) =>
            array.findIndex(p => p?.identificadorProcesso === processo?.identificadorProcesso) === index
        );

        processosUnicos.forEach(processo => {
            runInAction(() => {
                processo.pendencia = '';
            });
        });
    }

}

export default new PendenciasProcessoService();
